import { useState } from "react";
import { roteiroAPI } from "../lib/api";
import { Sparkles, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PILARES = [
    { value: "musica",          label: "🎤 Música",          desc: "Canções, clipes, bastidores" },
    { value: "fe",              label: "🙏 Fé",              desc: "Devocionais, reflexões bíblicas" },
    { value: "mulheres",        label: "👩 Mulheres",         desc: "Identidade feminina, comunidade" },
    { value: "proposito",       label: "✨ Propósito",        desc: "Missão de vida, chamado" },
    { value: "mentorias",       label: "👑 Mentorias",        desc: "Mulheres c/ Propósito, Mesa de Ester" },
    { value: "historia_pessoal",label: "📖 História Pessoal", desc: "Vivências, testemunho" },
];

const TIPOS = [
    { value: "blog",      label: "Post de Blog",       icon: "📝" },
    { value: "carrossel", label: "Carrossel Instagram", icon: "🎠" },
    { value: "reels",     label: "Script de Reels",    icon: "🎬" },
    { value: "devocional",label: "Devocional",         icon: "✝️" },
    { value: "email",     label: "Email / Newsletter",  icon: "📧" },
    { value: "whatsapp",  label: "Mensagem WhatsApp",   icon: "💬" },
];

const TONS = [
    { value: "inspirador",   label: "Inspirador" },
    { value: "testemunhal",  label: "Testemunhal" },
    { value: "espiritual",   label: "Espiritual" },
    { value: "pratico",      label: "Prático" },
    { value: "acolhedor",    label: "Acolhedor" },
    { value: "convidativo",  label: "Convidativo" },
];

export default function GeradorRoteiros() {
    const [pilar, setPilar] = useState("proposito");
    const [tipo, setTipo] = useState("blog");
    const [topic, setTopic] = useState("");
    const [tom, setTom] = useState("inspirador");
    const [result, setResult] = useState("");
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [history, setHistory] = useState([]);

    const gerar = async () => {
        if (!topic.trim()) { toast.error("Informe o tema do conteúdo"); return; }
        setLoading(true);
        try {
            const fullTopic = `${topic} (tom: ${tom})`;
            const r = await roteiroAPI.gerar({ pilar, topic: fullTopic, tipo });
            const content = r.data.content || "";
            setResult(content);
            setHistory(h => [{ pilar, tipo, topic, tom, content, ts: new Date().toLocaleTimeString("pt-BR") }, ...h.slice(0, 4)]);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erro ao gerar. Configure OPENAI_API_KEY.");
        } finally { setLoading(false); }
    };

    const copy = async () => {
        await navigator.clipboard.writeText(result);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success("Copiado!");
    };

    return (
        <div className="p-6 space-y-6 max-w-5xl">
            <div>
                <h1 className="text-2xl font-bold text-stone-100 flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-amber-500" /> Gerador de Roteiros
                </h1>
                <p className="text-stone-400 text-sm mt-1">Cria conteúdo com a voz de marca da Tayane Larini</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Formulário */}
                <div className="space-y-5">
                    {/* Pilar */}
                    <div>
                        <label className="text-xs text-stone-400 font-medium block mb-2">Pilar editorial</label>
                        <div className="grid grid-cols-2 gap-2">
                            {PILARES.map(p => (
                                <button key={p.value} onClick={() => setPilar(p.value)}
                                    className={`text-left p-3 rounded-xl border transition-all ${pilar === p.value ? "border-amber-600 bg-amber-900/20" : "border-stone-700 hover:border-stone-600"}`}>
                                    <p className={`text-xs font-medium ${pilar === p.value ? "text-amber-300" : "text-stone-300"}`}>{p.label}</p>
                                    <p className="text-xs text-stone-500 mt-0.5">{p.desc}</p>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tipo de conteúdo */}
                    <div>
                        <label className="text-xs text-stone-400 font-medium block mb-2">Tipo de conteúdo</label>
                        <div className="grid grid-cols-3 gap-2">
                            {TIPOS.map(t => (
                                <button key={t.value} onClick={() => setTipo(t.value)}
                                    className={`p-2.5 rounded-xl border text-center transition-all ${tipo === t.value ? "border-amber-600 bg-amber-900/20" : "border-stone-700 hover:border-stone-600"}`}>
                                    <span className="text-lg block">{t.icon}</span>
                                    <p className={`text-xs mt-1 ${tipo === t.value ? "text-amber-300" : "text-stone-400"}`}>{t.label}</p>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tom */}
                    <div>
                        <label className="text-xs text-stone-400 font-medium block mb-2">Tom</label>
                        <div className="flex flex-wrap gap-2">
                            {TONS.map(t => (
                                <button key={t.value} onClick={() => setTom(t.value)}
                                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tom === t.value ? "bg-amber-800/40 border-amber-700 text-amber-300" : "border-stone-700 text-stone-400 hover:text-stone-200"}`}>
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tema */}
                    <div>
                        <label className="text-xs text-stone-400 font-medium block mb-2">Tema / assunto</label>
                        <textarea rows={3} className="w-full bg-stone-900 border border-stone-800 rounded-xl px-4 py-3 text-sm text-stone-200 focus:outline-none focus:border-amber-600 resize-none"
                            placeholder="ex: como encontrar propósito mesmo no meio da dor..."
                            value={topic} onChange={e => setTopic(e.target.value)}
                            onKeyDown={e => e.ctrlKey && e.key === "Enter" && gerar()} />
                        <p className="text-xs text-stone-600 mt-1">Ctrl+Enter para gerar</p>
                    </div>

                    <button onClick={gerar} disabled={loading || !topic.trim()}
                        className="w-full flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-600 text-white py-3 rounded-xl font-medium disabled:opacity-50 transition-colors">
                        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Gerando...</> : <><Sparkles className="w-4 h-4" /> Gerar conteúdo</>}
                    </button>
                </div>

                {/* Resultado */}
                <div className="space-y-4">
                    <div className="relative">
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs text-stone-400 font-medium">Conteúdo gerado</label>
                            {result && (
                                <button onClick={copy}
                                    className="flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-200 border border-stone-700 rounded-lg px-2.5 py-1 transition-colors">
                                    {copied ? <><Check className="w-3 h-3 text-green-400" /> Copiado!</> : <><Copy className="w-3 h-3" /> Copiar</>}
                                </button>
                            )}
                        </div>
                        <textarea className="w-full bg-stone-900 border border-stone-800 rounded-xl px-4 py-3 text-sm text-stone-300 focus:outline-none resize-none leading-relaxed"
                            style={{ minHeight: "350px" }}
                            readOnly value={loading ? "Gerando conteúdo..." : result}
                            placeholder="O roteiro gerado aparecerá aqui..." />
                    </div>

                    {/* Histórico */}
                    {history.length > 0 && (
                        <div>
                            <p className="text-xs text-stone-500 font-medium mb-2">Gerados recentemente</p>
                            <div className="space-y-2">
                                {history.map((h, i) => (
                                    <button key={i} onClick={() => setResult(h.content)}
                                        className="w-full text-left bg-stone-900 border border-stone-800 rounded-lg p-3 hover:border-stone-700 transition-colors">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xs text-amber-400">{TIPOS.find(t => t.value === h.tipo)?.icon}</span>
                                            <span className="text-xs text-stone-300 truncate">{h.topic}</span>
                                            <span className="text-xs text-stone-600 ml-auto flex-shrink-0">{h.ts}</span>
                                        </div>
                                        <p className="text-xs text-stone-500 truncate">{h.content.slice(0, 80)}...</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
